import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  Code2, 
  Palette, 
  Database, 
  Zap,
  ArrowRight,
  Check,
  ChevronRight,
  Settings,
  Target,
  FileText,
  TestTube,
  Upload,
  Wrench,
  TrendingUp,
  Share2,
  Search,
  Award
} from 'lucide-react';

const benefitIcons = [Code2, Palette, Database, Zap];
const stepIcons = [Target, FileText, Calculator, Wrench, TestTube, Upload];
const seoIcons = [TrendingUp, Share2, Search, Award];

export default function BuildYourOwn() {
  const { t, i18n } = useTranslation('build-your-own');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('build-your-own', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              <Calculator className="w-4 h-4 mr-2 inline" />
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto" data-testid="text-description">
              {t('hero.description')}
            </p>
            
            <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-hero">
              {t('hero.cta')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>

          {/* Why Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-4 text-center" data-testid="title-why">{t('why.title')}</h2>
            <p className="text-lg text-muted-foreground mb-8 text-center max-w-3xl mx-auto" data-testid="text-why-description">
              {t('why.description')}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {(t('why.benefits', { returnObjects: true }) as any[]).map((benefit: any, index: number) => {
                const Icon = benefitIcons[index] || Check;
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-benefit-${index}`}>
                            {benefit.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-benefit-${index}`}>
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* What Is Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-4" data-testid="title-what">{t('what.title')}</h2>
            <p className="text-lg text-muted-foreground mb-6" data-testid="text-what-description">
              {t('what.description')}
            </p>
            
            <Card className="mb-6">
              <CardContent className="p-8">
                <ul className="space-y-3 mb-6">
                  {(t('what.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start" data-testid={`list-feature-${index}`}>
                      <Check className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg font-medium" data-testid="text-what-conclusion">{t('what.conclusion')}</p>
              </CardContent>
            </Card>
          </section>

          {/* How Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-how">{t('how.title')}</h2>
            
            <div className="space-y-6">
              {(t('how.steps', { returnObjects: true }) as any[]).map((step: any, index: number) => {
                const Icon = stepIcons[index] || Settings;
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl font-bold text-primary/30">{step.number}</span>
                            <h3 className="text-xl font-semibold" data-testid={`title-step-${index}`}>
                              {step.title}
                            </h3>
                          </div>
                          <p className="text-muted-foreground" data-testid={`text-step-${index}`}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Case Study Section */}
          <section className="mb-16">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-4 text-center" data-testid="title-case">{t('case.title')}</h2>
                <p className="text-lg text-center mb-6" data-testid="text-case-company">{t('case.company')}</p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  {(t('case.results', { returnObjects: true }) as string[]).map((result: string, index: number) => (
                    <div key={index} className="text-center" data-testid={`result-${index}`}>
                      <div className="text-3xl font-bold text-primary mb-2">
                        {result.split(' ')[0]}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {result.split(' ').slice(1).join(' ')}
                      </p>
                    </div>
                  ))}
                </div>
                
                <p className="text-center text-muted-foreground italic" data-testid="text-case-conclusion">
                  {t('case.conclusion')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Tips Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-tips">{t('tips.title')}</h2>
            
            <div className="space-y-4">
              {(t('tips.items', { returnObjects: true }) as any[]).map((tip: any, index: number) => (
                <Card key={index}>
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" data-testid={`title-tip-${index}`}>{tip.title}</h3>
                      <p className="text-muted-foreground" data-testid={`text-tip-${index}`}>{tip.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Industries Table Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-industries">{t('industries.title')}</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-card rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-primary/10">
                    <th className="p-4 text-left font-semibold">
                      {i18n.language === 'en' ? 'Industry' : i18n.language === 'da' ? 'Branche' : i18n.language === 'cs' ? 'Odvětví' : 'Odvetvie'}
                    </th>
                    <th className="p-4 text-left font-semibold">
                      {i18n.language === 'en' ? 'Calculator' : i18n.language === 'da' ? 'Beregner' : i18n.language === 'cs' ? 'Kalkulačka' : 'Kalkulačka'}
                    </th>
                    <th className="p-4 text-left font-semibold">
                      {i18n.language === 'en' ? 'Benefit' : i18n.language === 'da' ? 'Fordel' : i18n.language === 'cs' ? 'Výhoda' : 'Výhoda'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(t('industries.items', { returnObjects: true }) as any[]).map((item: any, index: number) => (
                    <tr key={index} className="border-t border-border" data-testid={`row-industry-${index}`}>
                      <td className="p-4 font-medium">{item.industry}</td>
                      <td className="p-4 text-muted-foreground">{item.calculator}</td>
                      <td className="p-4 text-muted-foreground">{item.benefit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* SEO Benefits Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-4 text-center" data-testid="title-seo">{t('seo.title')}</h2>
            <p className="text-lg text-muted-foreground mb-8 text-center" data-testid="text-seo-description">
              {t('seo.description')}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {(t('seo.benefits', { returnObjects: true }) as any[]).map((benefit: any, index: number) => {
                const Icon = seoIcons[index] || Check;
                return (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold mb-1" data-testid={`title-seo-benefit-${index}`}>
                            {benefit.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-seo-benefit-${index}`}>
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Why PriceForms Section */}
          <section className="mb-16">
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-4 text-center" data-testid="title-why-priceforms">
                  {t('whyPriceForms.title')}
                </h2>
                <p className="text-lg text-center mb-6" data-testid="text-why-priceforms-description">
                  {t('whyPriceForms.description')}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                  {(t('whyPriceForms.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <div key={index} className="flex items-center gap-2" data-testid={`feature-${index}`}>
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center" data-testid="title-faq">{t('faq.title')}</h2>
            <div className="space-y-6">
              {(t('faq.items', { returnObjects: true }) as any[]).map((item: any, index: number) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3" data-testid={`question-${index}`}>
                      ❓ {item.question}
                    </h3>
                    <p className="text-muted-foreground" data-testid={`answer-${index}`}>{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="text-center">
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-4" data-testid="title-final-cta">{t('cta.title')}</h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto" data-testid="text-final-cta-description">
                  {t('cta.description')}
                </p>
                <div className="flex flex-col items-center gap-4">
                  <Button size="lg" onClick={handleCTAClick} className="text-lg px-8 py-6" data-testid="button-cta-final">
                    {t('cta.cta')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-sm text-muted-foreground" data-testid="text-guarantee">{t('cta.guarantee')}</p>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="build-your-own" faqKey="faq.items" />
      <Footer />
    </>
  );
}
